import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

interface ImageUploadFieldProps {
  /** Текущий URL изображения (с сервера) */
  value: string | null
  /** Выбран новый файл (null — удалить изображение) */
  onChange: (file: File | null) => void
  label: string
  disabled?: boolean
}

/**
 * Поле выбора изображения с локальным превью (data-URL).
 * Файл не загружается сам — отдаётся наружу через onChange
 * (загрузка после сохранения сущности, как аватар в ProfileEditDialog).
 */
export function ImageUploadField({ value, onChange, label, disabled }: ImageUploadFieldProps) {
  const { t } = useTranslation()
  const [preview, setPreview] = useState<string | null>(value)

  useEffect(() => {
    setPreview(value)
  }, [value])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !file.type.startsWith('image/')) return

    const reader = new FileReader()
    reader.onload = () => setPreview(typeof reader.result === 'string' ? reader.result : null)
    reader.readAsDataURL(file)
    onChange(file)
  }

  const handleClear = () => {
    setPreview(null)
    onChange(null)
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {preview && (
          <Box
            component="img"
            src={preview}
            alt={label}
            sx={{
              width: 120,
              height: 68,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        )}
        <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }} disabled={disabled}>
          {preview ? t('common.imageUpload.replace') : t('common.imageUpload.choose')}
          <input type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Button>
        {preview && (
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            sx={{ textTransform: 'none' }}
            onClick={handleClear}
            disabled={disabled}
          >
            {t('common.imageUpload.remove')}
          </Button>
        )}
      </Box>
    </Box>
  )
}
